import { User, CreditCard, LogOut, CircleHelp, MessageSquare, Menu, X, ChevronRight, Sun, Moon } from "lucide-react";
import BMLogo from "../../assets/Logo of CICO time (1).png";
import { useSelector, useDispatch } from "react-redux";
import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ACCOUNT_TYPE } from "../../utils/Account_Type";
import { Logout } from "../../services/oprations/Auth";
import { useTheme } from "../../Context/themeContext";

const Header = () => {
    const [isProfileOpen, setIsProfileOpen] = useState(false);
    const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.profile);
    const userData = typeof user === "string" ? JSON.parse(user) : user;
    const { theme, toggleTheme } = useTheme();

    const headerRef = useRef(null);

    useEffect(() => {
        const el = headerRef.current;
        if (!el) return;
        const setHeaderHeight = () => {
            document.documentElement.style.setProperty('--header-height', `${el.offsetHeight}px`);
        };
        setHeaderHeight();
        const observer = new ResizeObserver(setHeaderHeight);
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    // Close dropdowns on outside click
    React.useEffect(() => {
        const handleClickOutside = (event) => {
            if (isProfileOpen && !event.target.closest(".profile-container")) {
                setIsProfileOpen(false);
            }
            if (isMobileNavOpen && !event.target.closest(".mobile-nav-container")) {
                setIsMobileNavOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isProfileOpen, isMobileNavOpen]);

    function handlerClick(navigation) {
        setIsMobileNavOpen(false);
        setIsProfileOpen(false);

        const routes = {
            Home: "/",
            About: "/about",
            Product: "/product",
            Contact: "/contact",
            Help: "/help",
            Feedback: "/send-feedback",
            Billing: "/billing-plan",
            "Billing-Plan": "/billing-and-plan",
            login: "/login",
        };

        if (routes[navigation]) {
            navigate(routes[navigation]);
            return;
        }

        if (navigation === "dashboard") {
            if (userData?.role === ACCOUNT_TYPE.MANAGER || userData?.role === ACCOUNT_TYPE.ORGANIZATION) {
                navigate("/dashboard");
            }
        }

        if (navigation === "logout") {
            dispatch(Logout(navigate));
        }
    }

    const navLinks = [
        { label: "Home", key: "Home" },
        { label: "About", key: "About" },
        { label: "Products", key: "Product" },
        { label: "Contact", key: "Contact" },
        { label: "Help", key: "Help" },
    ];

    const isUser = userData?.role === ACCOUNT_TYPE.USER;

    return (
            <header ref={headerRef} className={`fixed w-full z-50 backdrop-blur-xl border-b transition-colors duration-300 ${
                theme === 'dark' 
                    ? 'bg-[#0B0F1C]/80 border-white/10' 
                    : 'bg-white/80 border-gray-200'
            }`}>
                <nav className="container flex items-center justify-between gap-2 px-3 py-3 mx-auto sm:gap-4 sm:px-6 sm:py-4">

                    {/* Logo */}
                    <div className="flex items-center flex-shrink-0 min-w-0 gap-2 group sm:gap-3">
                        <div className="relative flex-shrink-0">
                            <div className={`absolute inset-0 transition-opacity duration-500 opacity-0 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-xl sm:rounded-2xl blur-lg group-hover:opacity-60`}></div>
                            <div className="relative w-9 h-9 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl overflow-hidden bg-gradient-to-br from-blue-500 to-cyan-500 p-[2px] shadow-2xl">
                                <div className="flex items-center justify-center w-full h-full overflow-hidden bg-white rounded-xl sm:rounded-2xl">
                                    <img
                                        src={BMLogo}
                                        alt="CICO-Time Logo"
                                        className="object-contain w-full h-full transition-transform duration-500 transform group-hover:scale-110"
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-col min-w-0">
                            <span className="text-lg font-bold tracking-tight truncate sm:text-2xl">
                                <span className={`text-transparent bg-gradient-to-r ${
                                    theme === 'dark' 
                                        ? 'from-white to-blue-100' 
                                        : 'from-gray-800 to-blue-600'
                                } bg-clip-text`}>CICO</span>
                                <span className="text-transparent bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text">-Time</span>
                            </span>
                        </div>
                    </div>

                    {/* Desktop Navigation Links */}
                    {!userData?.role && (
                        <div className="items-center hidden gap-8 md:flex">
                            {navLinks.map(({ label, key }) => (
                                <button type="button"
                                    key={key}
                                    onClick={() => handlerClick(key)}
                                    className={`relative text-sm transition-colors cursor-pointer group ${
                                        theme === 'dark'
                                            ? 'text-blue-100 hover:text-white'
                                            : 'text-gray-600 hover:text-gray-900'
                                    }`}
                                >
                                    {label}
                                    <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-blue-400 to-cyan-400 group-hover:w-full transition-all duration-300"></span>
                                </button>
                            ))}
                        </div>
                    )}    

                    {/* Right Side */}
                    <div className="flex items-center flex-shrink-0 gap-1.5 sm:gap-3">
                        {/* Theme Toggle Button */}
                        <button
                            onClick={toggleTheme}
                            className={`p-2 sm:p-2.5 rounded-lg sm:rounded-xl transition-all duration-300 transform hover:scale-105 flex-shrink-0 ${
                                theme === 'dark'
                                    ? 'bg-white/5 hover:bg-white/10 text-yellow-400 border border-white/10'
                                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-200'
                            }`}
                            aria-label="Toggle theme"
                        >
                            {theme === 'dark' ? (
                                <Sun className="w-4 h-4" />
                            ) : (
                                <Moon className="w-4 h-4" />
                            )}
                        </button>

                        {/* Login Button (unauthenticated) */}
                        {!userData?.role && (
                            <button
                                onClick={() => handlerClick("login")}
                                className="flex items-center gap-2 px-3 py-2 text-xs font-medium text-white transition-all duration-300 transform shadow-lg sm:px-5 sm:py-2.5 rounded-lg sm:rounded-xl bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 sm:text-sm shadow-blue-600/25 hover:shadow-blue-600/40 hover:scale-105 whitespace-nowrap"
                            >
                                <i className="fas fa-user-plus"></i>
                                <span className="hidden xs:inline">Login Now</span>
                                <span className="xs:hidden">Login</span>
                            </button>
                        )}

                        {/* USER role: static avatar + name + badge, no dropdown, no click */}
                        {isUser && (
                            <div className={`flex items-center gap-2 pl-2 pr-2.5 py-1 sm:gap-3 sm:pl-3 sm:pr-4 sm:py-1.5 rounded-lg sm:rounded-xl min-w-0 max-w-[150px] sm:max-w-none ${
                                theme === 'dark'
                                    ? 'bg-white/5 border-white/10'
                                    : 'bg-gray-100/80 border-gray-200'
                            } border`}>
                                <div className="relative flex-shrink-0 w-8 h-8 rounded-lg sm:w-9 sm:h-9 sm:rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 p-[2px] shadow-lg">
                                    <div className={`w-full h-full rounded-lg sm:rounded-xl flex items-center justify-center ${
                                        theme === 'dark' ? 'bg-[#0B0F1C]' : 'bg-white'
                                    }`}>
                                        <User className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`} />
                                    </div>
                                    <span className="absolute -top-0.5 -right-0.5 w-2 h-2 sm:w-2.5 sm:h-2.5 bg-green-500 border-2 border-[#0B0F1C] rounded-full"></span>
                                </div>
                                <div className="flex flex-col min-w-0">
                                    <span className={`text-xs sm:text-sm font-medium leading-tight truncate ${
                                        theme === 'dark' ? 'text-white' : 'text-gray-800'
                                    }`}>{userData?.user_name}</span>
                                    <span className="text-[9px] sm:text-[10px] dark:text-green-300 text-green-700 leading-tight">USER</span>
                                </div>
                            </div>
                        )}

                        {/* Profile Dropdown (Organization / Manager only) */}
                        {userData?.role && !isUser && (
                            <div className="relative flex-shrink-0 profile-container">
                                <button
                                    onClick={() => setIsProfileOpen(!isProfileOpen)}
                                    className="relative flex-shrink-0 w-9 h-9 transition-all duration-300 transform shadow-lg sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 p-[2px] hover:shadow-blue-500/30 hover:scale-105"
                                >
                                    <div className={`w-full h-full rounded-lg sm:rounded-xl flex items-center justify-center transition-colors duration-300 ${
                                        theme === 'dark'
                                            ? 'bg-[#0B0F1C] hover:bg-[#1a1f2e]'
                                            : 'bg-white hover:bg-gray-50'
                                    }`}>
                                        <User className={`w-4 h-4 ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`} />
                                    </div>
                                    <span className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-green-500 border-2 border-[#0B0F1C] rounded-full"></span>
                                </button>

                                {isProfileOpen && (
                                    <>
                                        <div className="fixed inset-0 z-40 bg-black/20 backdrop-blur-sm" onClick={() => setIsProfileOpen(false)}></div>
                                        <div className="absolute z-50 right-0 sm:-right-10 top-12 sm:top-14 w-[calc(100vw-1.5rem)] max-w-[320px] sm:w-80 animate-slide-down">
                                            <div className={`rounded-2xl border shadow-2xl backdrop-blur-xl overflow-hidden transition-colors duration-300 ${
                                                theme === 'dark'
                                                    ? 'bg-gradient-to-br from-[#1a1f2e] to-[#0B0F1C] border-white/10'
                                                    : 'bg-gradient-to-br from-white to-gray-50 border-gray-200'
                                            }`}>
                                                {/* Profile Header */}
                                                <div className={`relative p-4 sm:p-6 border-b transition-colors duration-300 ${
                                                    theme === 'dark'
                                                        ? 'bg-gradient-to-r from-blue-600/20 to-cyan-600/20 border-white/10'
                                                        : 'bg-gradient-to-r from-blue-50 to-cyan-50 border-gray-200'
                                                }`}>
                                                    <div className={`absolute inset-0 ${
                                                        theme === 'dark'
                                                            ? 'bg-gradient-to-r from-blue-600/10 to-cyan-600/10'
                                                            : 'bg-gradient-to-r from-blue-50/50 to-cyan-50/50'
                                                    }`}></div>
                                                    <div className="relative flex items-center gap-3 sm:gap-4">
                                                        <div className="relative flex-shrink-0">
                                                            <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 p-[2px]">
                                                                <div className={`flex items-center justify-center w-full h-full rounded-2xl ${
                                                                    theme === 'dark' ? 'bg-blue-900' : 'bg-blue-50/10'
                                                                }`}>
                                                                    <span className={`text-[32px] sm:text-[40px] ${
                                                                        theme === 'dark' ? 'text-white' : 'text-gray-300'
                                                                    }`}>
                                                                        {userData?.manager_name?.slice(0, 1) || userData?.org_name?.slice(0, 1)}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                            <span className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 border-2 border-[#0B0F1C] rounded-full"></span>
                                                        </div>
                                                        <div className="min-w-0">
                                                            <h3 className={`text-base sm:text-lg font-bold truncate ${
                                                                theme === 'dark' ? 'text-white' : 'text-gray-800'
                                                            }`}>{userData?.manager_name || userData?.org_name}</h3>
                                                            <p className={`text-xs truncate ${
                                                                theme === 'dark' ? 'text-blue-200/70' : 'text-gray-600'
                                                            }`}>{userData?.manager_email || userData?.org_email}</p>
                                                            <div className="flex items-center gap-1 mt-1">
                                                                <span className="text-[10px] dark:bg-green-600/30 bg-green-300/30 dark:text-green-300 text-green-800 px-2 py-0.5 rounded-full border dark:border-green-500/30 border-green-700">
                                                                    {userData?.role}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Menu Items */}
                                                <div className="p-3 space-y-1">
                                                    <button onClick={() => handlerClick("dashboard")} className={`flex items-center w-full gap-3 px-4 py-3 text-sm transition-all duration-300 rounded-xl group ${
                                                        theme === 'dark'
                                                            ? 'text-blue-100 hover:text-white hover:bg-white/5'
                                                            : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                                                    }`}>
                                                        <div className={`flex items-center justify-center w-8 h-8 transition-colors rounded-lg ${
                                                            theme === 'dark'
                                                                ? 'bg-blue-600/20 group-hover:bg-blue-600/30'
                                                                : 'bg-blue-100/30 group-hover:bg-blue-200/20'
                                                        }`}>
                                                            <User className={`w-4 h-4 ${theme === 'dark' ? 'text-blue-300' : 'text-blue-600'}`} />
                                                        </div>
                                                        <span className="flex-1 text-left">My Profile</span>
                                                    </button>

                                                    {userData?.role === ACCOUNT_TYPE.MANAGER && userData?.access === true && (
                                                        <button onClick={() => handlerClick("Billing-Plan")} className={`flex items-center w-full gap-3 px-4 py-3 text-sm transition-all duration-300 rounded-xl group ${
                                                            theme === 'dark'
                                                                ? 'text-blue-100/80 hover:text-white hover:bg-white/5'
                                                                : 'text-gray-700/80 hover:text-gray-900 hover:bg-gray-100'
                                                        }`}>
                                                            <div className={`flex items-center justify-center w-8 h-8 transition-colors rounded-lg ${
                                                                theme === 'dark'
                                                                    ? 'bg-blue-600/20 group-hover:bg-blue-600/30'
                                                                    : 'bg-blue-100/30 group-hover:bg-blue-200/20'
                                                            }`}>
                                                                <CreditCard className={`w-4 h-4 ${theme === 'dark' ? 'text-blue-300' : 'text-blue-600'}`} />
                                                            </div>
                                                            <span className="flex-1 text-left">Billing & Plan</span>
                                                            <span className="text-[10px] bg-gradient-to-r from-blue-600 to-cyan-600 text-white px-2 py-1 rounded-full">Business</span>
                                                        </button>
                                                    )}

                                                    <button onClick={() => handlerClick("Help")} className={`flex items-center w-full gap-3 px-4 py-3 text-sm transition-all duration-300 rounded-xl group ${
                                                        theme === 'dark'
                                                            ? 'text-blue-100/80 hover:text-white hover:bg-white/5'
                                                            : 'text-gray-700/80 hover:text-gray-900 hover:bg-gray-100'
                                                    }`}>
                                                        <div className={`flex items-center justify-center w-8 h-8 transition-colors rounded-lg ${
                                                            theme === 'dark'
                                                                ? 'bg-blue-600/20 group-hover:bg-blue-600/30'
                                                                : 'bg-blue-100/30 group-hover:bg-blue-200/30'
                                                        }`}>
                                                            <CircleHelp className={`w-4 h-4 ${theme === 'dark' ? 'text-blue-300' : 'text-blue-600'}`} />
                                                        </div>
                                                        <span className="flex-1 text-left">Help</span>
                                                    </button>

                                                    <button onClick={() => handlerClick("Feedback")} className={`flex items-center w-full gap-3 px-4 py-3 text-sm transition-all duration-300 rounded-xl group ${
                                                        theme === 'dark'
                                                            ? 'text-blue-100/80 hover:text-white hover:bg-white/5'
                                                            : 'text-gray-700/80 hover:text-gray-900 hover:bg-gray-100'
                                                    }`}>
                                                        <div className={`flex items-center justify-center w-8 h-8 transition-colors rounded-lg ${
                                                            theme === 'dark'
                                                                ? 'bg-blue-600/20 group-hover:bg-blue-600/30'
                                                                : 'bg-blue-100/30 group-hover:bg-blue-200/30'
                                                        }`}>
                                                            <MessageSquare className={`w-4 h-4 ${theme === 'dark' ? 'text-blue-300' : 'text-blue-600'}`} />
                                                        </div>
                                                        <span className="flex-1 text-left">Send Feedback</span>
                                                        <ChevronRight className={`w-3 h-3 ${theme === 'dark' ? 'text-blue-400/50' : 'text-gray-400'}`} />
                                                    </button>
                                                </div>

                                                <div className={`my-2 border-t ${theme === 'dark' ? 'border-white/10' : 'border-gray-200'}`}></div>

                                                <div className="p-3">
                                                    <button onClick={() => handlerClick("logout")} className="flex items-center w-full gap-3 px-4 py-3 text-sm transition-all duration-300 text-red-400/80 hover:text-red-400 hover:bg-red-500/10 rounded-xl group">
                                                        <div className="flex items-center justify-center w-8 h-8 transition-colors rounded-lg bg-red-500/20 group-hover:bg-red-500/30">
                                                            <LogOut className="w-4 h-4 text-red-400" />
                                                        </div>
                                                        <span className="flex-1 text-left">Sign Out</span>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        )}

                        {/* Mobile Hamburger — visible only on small screens */}
                        {!userData?.role && (
                            <div className="flex-shrink-0 mobile-nav-container md:hidden">
                                <button
                                    onClick={() => setIsMobileNavOpen(!isMobileNavOpen)}
                                    className={`flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 transition-all duration-300 border rounded-lg sm:rounded-xl ${
                                        theme === 'dark'
                                            ? 'text-blue-200 bg-white/5 border-white/10 hover:text-white hover:bg-white/10'
                                            : 'text-gray-600 bg-gray-50 border-gray-200 hover:text-gray-900 hover:bg-gray-100'
                                    }`}
                                    aria-label="Toggle navigation"
                                >
                                    {isMobileNavOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                                </button>

                                {/* Mobile Nav Dropdown */}
                                {isMobileNavOpen && (
                                    <>
                                        <div className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm" onClick={() => setIsMobileNavOpen(false)}></div>
                                        <div className="absolute right-0 z-50 w-56 mt-3 top-full">
                                            <div className={`rounded-2xl border shadow-2xl overflow-hidden transition-colors duration-300 ${
                                                theme === 'dark'
                                                    ? 'bg-gradient-to-br from-[#1a1f2e] to-[#0B0F1C] border-white/10'
                                                    : 'bg-white border-gray-200'
                                            }`}>
                                                <div className="p-2 space-y-0.5">
                                                    {navLinks.map(({ label, key }) => (
                                                        <button
                                                            key={key}
                                                            onClick={() => handlerClick(key)}
                                                            className={`flex items-center w-full gap-3 px-4 py-3 text-sm transition-all duration-200 rounded-xl ${
                                                                theme === 'dark'
                                                                    ? 'text-blue-100 hover:text-white hover:bg-white/5'
                                                                    : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                                                            }`}
                                                        >
                                                            <ChevronRight className={`w-3.5 h-3.5 ${
                                                                theme === 'dark' ? 'text-blue-400/60' : 'text-gray-400'
                                                            }`} />
                                                            {label}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        )}   
                    </div>
                </nav>
            </header>
    );
};

export default Header;
